import React, { createContext, useReducer, useMemo, useContext, useCallback } from "react";

// Create a context for location data
export const LocationContext = createContext(null);

const STORAGE_KEY = "raahiSathi_locationState";


// Load persisted state from localStorage
const loadPersistedState = () => {
  try {
    const savedState = localStorage.getItem(STORAGE_KEY);
    return savedState ? JSON.parse(savedState) : null;
  } catch (error) {
    console.error("Error loading persisted state:", error);
    return null;
  }
};

// Initial state for the location context
export const initialState = {
  pickUp: "",
  drop: "",
  additionalDrops: [],
  date: "",
  returnDate: "",
  time: "",
  tripType: "",
  airportType: "",
  selectedCar: "",
  localRideOption: "",
};

// Action types for the reducer
export const ACTION_TYPES = {
  UPDATE_FIELD: "UPDATE_FIELD",
  UPDATE_TRIP_TYPE: "UPDATE_TRIP_TYPE",
  SELECT_CAR: "SELECT_CAR",
  RESET_FORM: "RESET_FORM",
};

// Reducer function to handle state updates
const locationReducer = (state, action) => {
  switch (action.type) {
    case ACTION_TYPES.UPDATE_FIELD: {
      const newState = { ...state, [action.field]: action.value };
      // localStorage.setItem(STORAGE_KEY, JSON.stringify(newState));
      return newState;
    }
    case ACTION_TYPES.UPDATE_TRIP_TYPE: {
      const newState = {
        ...state,
        tripType: action.tripType,
        airportType: action.tripType === "Airport" ? "Drop to Airport" : "",
        drop: action.tripType === "Local" ? "" : state.drop,
        additionalDrops: [], // This should correctly reset additionalDrops
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newState));
      return newState;
    }
    case ACTION_TYPES.SELECT_CAR: {
      const newState = { ...state, selectedCar: action.car };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newState));
      return newState;
    }
    case ACTION_TYPES.RESET_FORM: {
      localStorage.removeItem(STORAGE_KEY);
      return initialState;
    }
    default:
      return state;
  }
};

// Provider component to wrap the application and provide the context
export const LocationProvider = ({ children }) => {
  const [state, dispatch] = useReducer(
    locationReducer,
    loadPersistedState() || initialState
  );

  // Memoize actions to prevent unnecessary re-renders
  const updateField = useCallback((field, value) => {
    dispatch({ type: ACTION_TYPES.UPDATE_FIELD, field, value });
  }, []);

  const updateTripType = useCallback((tripType) => {
    dispatch({ type: ACTION_TYPES.UPDATE_TRIP_TYPE, tripType });
  }, []);

  const selectCar = useCallback((car) => {
    dispatch({ type: ACTION_TYPES.SELECT_CAR, car });
  }, []);

  const resetForm = useCallback(() => {
    dispatch({ type: ACTION_TYPES.RESET_FORM });
  }, []);

  // Memoize context value
  const value = useMemo(
    () => ({
      state,
      updateField,
      updateTripType,
      selectCar,
      resetForm,
    }),
    [state, updateField, updateTripType, selectCar, resetForm]
  );

  return (
    <LocationContext.Provider value={value}>
      {children}
    </LocationContext.Provider>
  );
};

// Custom hook to use the location context
export const useLocationContext = () => {
  const context = useContext(LocationContext);
  if (!context)
    throw new Error(
      "useLocationContext must be used within a LocationProvider"
    );
  return context;
};

export default LocationProvider;